import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  FileText,
  Download,
  Loader2,
  BookOpen,
  Shield,
  CreditCard,
  Server,
  Lock,
  FileSignature
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const DOC_ICONS = {
  platform: BookOpen,
  payment: CreditCard,
  security: Shield,
  privacy: Lock,
  infrastructure: Server,
  ownership: FileSignature
};

const DOC_DESCRIPTIONS = {
  platform: "Complete overview of the RTS Express platform, booking system, and admin functionality",
  payment: "Stripe integration, payment processing, receipts, and invoice handling",
  security: "Admin authentication, 2FA, activity logs, and access control",
  privacy: "Customer data collection, storage, GDPR compliance, and data protection",
  infrastructure: "Platform architecture, hosting, deployment, and backup procedures",
  ownership: "Software intellectual property declaration for company records"
};

const DOC_TITLES = {
  platform: "Platform System Documentation",
  payment: "Payment Processing Documentation",
  security: "Security Overview",
  privacy: "Data Handling & Privacy Overview",
  infrastructure: "Infrastructure & Deployment Overview",
  ownership: "Software Ownership Declaration"
};

export default function DocumentationPage() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (admin && admin.role !== "owner" && admin.role !== "admin") {
      navigate("/admin");
      toast.error("Acces restricționat la Documentație");
    }
  }, [admin, navigate]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/documentation");
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error("Eroare la încărcarea documentelor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleDownload = async (doc) => {
    try {
      setDownloading(doc.id);
      const response = await api.get(`/admin/documentation/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${doc.filename} descărcat`);
    } catch (error) {
      toast.error("Eroare la descărcare");
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    for (const doc of documents) {
      await handleDownload(doc);
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#22c55e]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Înapoi
            </Button>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-white">
              Company Documentation
            </h1>
          </div>
          {documents.length > 0 && (
            <Button
              onClick={handleDownloadAll}
              className="bg-[#22c55e] hover:bg-[#16a34a] text-black font-semibold"
            >
              <Download className="w-4 h-4 mr-2" />
              Download All
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <p className="text-slate-400">
            Official documentation package for RTS Express platform. These documents are formatted for 
            print and can be archived as official company records.
          </p>
        </div>

        {documents.length === 0 ? (
          <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-8 text-center">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No documentation files available</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {documents.map((doc) => {
              const Icon = DOC_ICONS[doc.id] || FileText;
              return (
                <div 
                  key={doc.id}
                  className="bg-[#0f172a] border border-slate-800 rounded-lg p-6 flex items-center gap-6 hover:border-slate-700 transition-colors"
                >
                  <div className="w-14 h-14 rounded-lg bg-[#22c55e]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-7 h-7 text-[#22c55e]" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {DOC_TITLES[doc.id] || doc.filename}
                    </h3>
                    <p className="text-slate-400 text-sm mb-2">
                      {DOC_DESCRIPTIONS[doc.id]}
                    </p>
                    <p className="text-slate-500 text-xs">
                      {doc.filename} • {doc.size_kb} KB
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => handleDownload(doc)}
                    disabled={downloading === doc.id}
                    variant="outline"
                    className="border-slate-700 text-slate-400 hover:text-white hover:border-[#22c55e]"
                  >
                    {downloading === doc.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 bg-slate-900/50 border border-slate-800 rounded-lg p-6">
          <h3 className="text-white font-semibold mb-2">Document Information</h3>
          <ul className="text-slate-400 text-sm space-y-1">
            <li>• Documents are generated in A4 format, ready for printing</li>
            <li>• Each document includes page numbers and confidentiality notices</li>
            <li>• The Software Ownership Declaration includes signature fields</li>
            <li>• All documents are dated for version control purposes</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
